import { useEffect, useRef } from 'react';
import { session } from './api.js';

const RETRY_MAX_MS = 10_000;

export function connectRealtime({ onEvent, onStatus }) {
  let stopped = false;
  let controller;
  let retryTimer;
  let retryAttempt = 0;

  const scheduleReconnect = () => {
    if (stopped) return;
    const delay = Math.min(1_000 * (2 ** retryAttempt), RETRY_MAX_MS);
    retryAttempt += 1;
    retryTimer = window.setTimeout(connect, delay);
  };

  const connect = async () => {
    const token = session.get();
    if (stopped || !token) return;
    controller = new AbortController();
    onStatus('connecting');
    try {
      const response = await fetch('/api/events', {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok || !response.body) throw new Error('REALTIME_UNAVAILABLE');
      onStatus('connected');
      retryAttempt = 0;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (!stopped) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true }).replaceAll('\r\n', '\n');
        let boundary = buffer.indexOf('\n\n');
        while (boundary >= 0) {
          const block = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          const data = block.split('\n').filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trimStart()).join('\n');
          if (data) {
            try { onEvent(JSON.parse(data)); } catch { /* Ignore malformed frames. */ }
          }
          boundary = buffer.indexOf('\n\n');
        }
      }
      if (!stopped) throw new Error('REALTIME_CLOSED');
    } catch (error) {
      if (stopped || error.name === 'AbortError') return;
      onStatus('offline');
      scheduleReconnect();
    }
  };

  const reconnectNow = () => {
    if (stopped) return;
    window.clearTimeout(retryTimer);
    controller?.abort();
    retryAttempt = 0;
    connect();
  };

  window.addEventListener('online', reconnectNow);
  connect();
  return () => {
    stopped = true;
    window.clearTimeout(retryTimer);
    controller?.abort();
    window.removeEventListener('online', reconnectNow);
  };
}

export function useSyncRefresh(syncEvent, resources, refresh) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const resourceKey = resources.join('|');
  useEffect(() => {
    if (!syncEvent?.revision) return;
    const affected = syncEvent.resources?.includes('*') || resources.some((resource) => syncEvent.resources?.includes(resource));
    if (affected) Promise.resolve(refreshRef.current()).catch(() => {});
  }, [syncEvent?.revision, resourceKey]);
}
